import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Button, Form, Input, Dropdown, Checkbox, Label, Modal } from "semantic-ui-react";
import { AppointmentMeHey } from "./AppointmentMeHeyUtils";
import { getAppointmentStatusInfo } from "./CardMakeAppointmentMeHey";
import { formatTimeFromDate } from "react-lib/utils/dateUtils";
import SearchBoxDropdown from "react-lib/appcon/common/SearchBoxDropdown";
import ModalMakeAppointmentError from "./ModalMakeAppointmentError";
import CardAppointmentPackage, { PackageSelectionState } from "../../HISV3/HCU/CardAppointmentPackage";
import { getValidPackageSelection, PackageSelectionItem } from "../../HISV3/HCU/sequence/packageSelection";
import { useIntl } from "react-intl";
import { AvailabilityBlock, getAvailableSlots, isAppointmentTimeAvailable } from "./appointmentAvailability";
import type { PackageAppointmentResult } from "../sequence/MakeAppointmentMeHey";

import "./PackageRightPanelMeHey.scss";

// ================ Types ================
type FormData = {
  appointmentId?: number;
  doctor?: string;
  date?: string;
  startTime?: string;
  endTime?: string;
  appointmentTime?: string;
  manualTime?: boolean;
  patientID?: number | string | null;
  patientName?: string;
  phoneNumber?: string;
};

type SelectedSchedule = AvailabilityBlock & {
  doctor_dsb_id?: number;
  doctor_provider_id?: number;
  doctor_provider_name?: string;
};
type SelectionHandle = {
  items: PackageSelectionItem[];
  checkedProductIds: number[];
  isReady: boolean;
};
type ReviewSnapshot = {
  form: FormData;
  dsb: SelectedSchedule;
  packageData: Record<string, unknown>;
  items: PackageSelectionItem[];
  checkedProductIds: number[];
  signature: string;
};
type LiveReview = {
  formData: FormData;
  selectedDsb?: SelectedSchedule;
  patient?: Record<string, unknown>;
  packageData?: Record<string, unknown>;
  scheduleLoading: boolean;
};

type PackageRightPanelMeHeyProps = {
  setProp: any;
  apiToken?: string | null;
  division?: number | string | null;
  languageUX?: string;
  onClose: () => void;
  editingAppointment?: AppointmentMeHey | null;
  selectedPatient?: Record<string, any>;
  selectedDoctorDSB?: (AppointmentMeHey & { dsb?: any }) | null;
  selectPackageBinder: any;
  onEvent: any;
  searchedItemListWithKey?: Record<string, any>;
  runSequence: any;
  MakeAppointmentMeHeySequence?: any;
  buttonLoadCheck: any;
  onAppointmentSuccess?: () => void;
};

// ================ Constants ================

const AMH = "AMH";
const KEY_PATIENT = `OfficialAccountPatientList_${AMH}`;

const DISABLED_STATUSES = ["ยกเลิกนัดหมาย", "ปฏิเสธโดย Provider", "เสร็จสิ้น"];
const EMPTY_PACKAGE_ITEMS: any[] = [];

// ================ Helpers ================

const formatTime = (date: Date): string =>
  `${date.getHours().toString().padStart(2, "0")}:${date.getMinutes().toString().padStart(2, "0")}`;

const formatDateDDMMYYYY = (date: Date): string =>
  `${date.getDate().toString().padStart(2, "0")}/${(date.getMonth() + 1)
    .toString()
    .padStart(2, "0")}/${date.getFullYear()}`;

const buildFormFromDSB = (
  selectedDoctorDSB: AppointmentMeHey & { dsb?: any }
): Pick<FormData, "doctor" | "date" | "startTime" | "endTime"> => {
  const dsb = selectedDoctorDSB.dsb;
  const { startTime, endTime } = selectedDoctorDSB;

  return {
    doctor: dsb.doctor_provider_id || "",
    date: startTime ? formatDateDDMMYYYY(startTime) : "",
    startTime: startTime ? formatTime(startTime) : "",
    endTime: endTime ? formatTime(endTime) : "",
  };
};

// ================ Component ================

const PackageRightPanelMeHey: React.FC<PackageRightPanelMeHeyProps> = (props) => {
  const intl = useIntl();
  const [packageSelection, setPackageSelection] = useState<PackageSelectionState>({
    isReady: false,
    selectedCount: 0,
  });
  const [formData, setFormData] = useState<FormData>({
    doctor: "",
    date: "",
    startTime: "",
    endTime: "",
    appointmentTime: "",
    manualTime: false,
    patientID: null,
    patientName: "",
    phoneNumber: "",
  });

  const [errors, setErrors] = useState<Record<string, boolean>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [confirmation, setConfirmation] = useState<ReviewSnapshot | null>(null);
  const [saveResult, setSaveResult] = useState<PackageAppointmentResult | null>(null);
  const submittingRef = useRef(false);
  const mountedRef = useRef(true);
  const liveRef = useRef<LiveReview | null>(null);
  const reviewTriggerRef = useRef<HTMLElement | null>(null);

  useEffect(() => () => { mountedRef.current = false; }, []);

  const handlePackageSelectionChange = useCallback((next: PackageSelectionState) => {
    setPackageSelection((current) =>
      current.isReady === next.isReady && current.selectedCount === next.selectedCount ? current : next,
    );
    if (next.isReady && next.selectedCount > 0) {
      setErrors((current) => current.packageItems ? { ...current, packageItems: false } : current);
    }
  }, []);

  const cardAppointmentPackageRef = useRef<SelectionHandle | null>(null);

  // ---- Derived values ----
  const availableSlots = props.selectedDoctorDSB?.dsb
    ? getAvailableSlots(props.selectedDoctorDSB.dsb)
    : null;
  const appointmentTimeSlots = (availableSlots || []).map((time) => ({
    key: time, text: time, value: time,
  }));

  const patientFullName = useMemo(() => {
    const detail = props.MakeAppointmentMeHeySequence?.selectedAppointmentDetail;
    if (!detail) return "";
    return [detail.patient_pre_name, detail.patient_first_name, detail.patient_last_name]
      .filter(Boolean)
      .join(" ");
  }, [props.MakeAppointmentMeHeySequence?.selectedAppointmentDetail]);

  const headerText = useMemo(() => {
    if (!props.selectedDoctorDSB?.startTime || !props.selectedDoctorDSB?.endTime) return "";

    const { startTime, endTime, title } = props.selectedDoctorDSB;
    const dateStr = formatDateDDMMYYYY(startTime);
    const startStr = `${startTime.getHours()}:${startTime
      .getMinutes()
      .toString()
      .padStart(2, "0")}`;
    const endStr = `${endTime.getHours()}.${endTime.getMinutes().toString().padStart(2, "0")}`;

    return `${dateStr} [${startStr} - ${endStr}] ${title || ""}`;
  }, [props.selectedDoctorDSB]);

  const statusInfo = useMemo(() => {
    if (!props.editingAppointment?.status) return null;
    return getAppointmentStatusInfo(props.editingAppointment.status);
  }, [props.editingAppointment?.status]);

  const isDisabledStatus = statusInfo ? DISABLED_STATUSES.includes(statusInfo.text) : false;

  const phoneNumber = useMemo(() => {
    if (props.editingAppointment) {
      return props.editingAppointment.tel || "-";
    }

    if (formData.patientID) {
      const patient = props.searchedItemListWithKey?.[KEY_PATIENT]?.find(
        (item: any) => item.id === formData.patientID
      );
      return patient?.phone_no || formData.phoneNumber || "-";
    }
    return "-";
  }, [props.editingAppointment, formData.patientID, props.searchedItemListWithKey]);

  // ---- Effects ----

  useEffect(() => {
    const selected = props.selectedDoctorDSB;
    if (!selected?.dsb || !selected.startTime || !selected.endTime) {
      setFormData({
        patientID: props.selectedPatient?.id || null,
        patientName: props.selectedPatient?.full_name || "",
        phoneNumber: props.selectedPatient?.main_contact_phone_no || "",
        appointmentTime: "",
        manualTime: false,
      });
      setConfirmation(null);
      setErrors({});
      return;
    }

    const base = buildFormFromDSB(selected);
    const appointmentTime = props.editingAppointment?.estimated_at
      ? formatTimeFromDate(new Date(props.editingAppointment.estimated_at))
      : "";
    setFormData({
      ...base,
      appointmentId: props.editingAppointment?.id,
      appointmentTime,
      manualTime: !!appointmentTime && !(getAvailableSlots(selected.dsb) || []).includes(appointmentTime),
      patientID: props.editingAppointment
        ? props.MakeAppointmentMeHeySequence?.selectedAppointmentDetail?.patient || null
        : props.selectedPatient?.id || null,
      patientName: props.editingAppointment ? patientFullName : props.selectedPatient?.full_name || "",
      phoneNumber: props.editingAppointment?.tel || props.selectedPatient?.main_contact_phone_no || "",
    });
    setConfirmation(null);
    setErrors({});
  }, [
    props.editingAppointment?.id,
    props.selectedDoctorDSB?.doctor_dsb_id,
    !!props.selectedDoctorDSB?.dsb,
    props.selectedDoctorDSB?.dsb?.date,
    props.selectedDoctorDSB?.dsb?.doctor_start_time,
    props.selectedDoctorDSB?.dsb?.doctor_end_time,
    props.selectedPatient?.id,
    props.selectedPatient?.full_name,
    patientFullName,
  ]);


  // ---- Handlers ----

  const handleChange = (field: keyof FormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: false }));
    }
  };

  const handleSelectedItem = useCallback(
    () => async (value: any) => {
      const patient = props.searchedItemListWithKey?.[KEY_PATIENT]?.find(
        (item: any) => item.id === value
      );

      if (patient) {
        const fullName = [patient.pre_name_th, patient.first_name_th, patient.last_name_th]
          .filter(Boolean)
          .join(" ");
        handleChange("patientName", fullName);
      }

      handleChange("patientID", value);
    },
    [props.searchedItemListWithKey]
  );

  const selectedDsb = props.selectedDoctorDSB?.dsb;
  const selection = cardAppointmentPackageRef.current;
  const validItems = getValidPackageSelection(selection?.items || [], selection?.checkedProductIds || []);
  const dateParts = (formData.date || "").split("/");
  const formDate = dateParts.length === 3 ? `${dateParts[2]}-${dateParts[1]}-${dateParts[0]}` : "";
  const validPatient = !!formData.patientID && !!formData.patientName &&
    String(formData.patientID) === String(props.selectedPatient?.id);
  const validSchedule = !!selectedDsb?.doctor_dsb_id &&
    selectedDsb.doctor_dsb_id === props.selectedDoctorDSB?.doctor_dsb_id &&
    !!selectedDsb.doctor_provider_id && !!selectedDsb.doctor_provider_name &&
    selectedDsb.date === formDate;
  const validTime = validSchedule && isAppointmentTimeAvailable(selectedDsb, formData.appointmentTime) &&
    (formData.manualTime || (!!formData.appointmentTime && !!availableSlots?.includes(formData.appointmentTime)));
  const validPackage = packageSelection.isReady && packageSelection.selectedCount > 0 &&
    validItems.length > 0 && validItems.length === new Set(selection?.checkedProductIds || []).size;
  const saveLocked = saveResult?.status === "partial" || saveResult?.status === "uncertain";
  const canSave = validPatient && validSchedule && validTime && validPackage &&
    !props.MakeAppointmentMeHeySequence?.AppointmentLoading && !isLoading && !saveLocked;

  const signature = (
    data: FormData, dsb: SelectedSchedule | undefined, patient: Record<string, unknown> | undefined,
    currentSelection: SelectionHandle | null, packageData: Record<string, unknown> | undefined,
  ) =>
    JSON.stringify({
      patient: data.patientID, name: data.patientName, patientProp: patient?.id, patientPropName: patient?.full_name,
      dsb: dsb?.doctor_dsb_id, provider: dsb?.doctor_provider_id, providerName: dsb?.doctor_provider_name,
      dsbDate: dsb?.date, date: data.date, time: data.appointmentTime, manual: data.manualTime,
      packageId: packageData?.id, ready: currentSelection?.isReady,
      items: getValidPackageSelection(currentSelection?.items || [], currentSelection?.checkedProductIds || []),
      checked: currentSelection?.checkedProductIds || [],
    });
  liveRef.current = {
    formData, selectedDsb, patient: props.selectedPatient, packageData: props.selectPackageBinder,
    scheduleLoading: !!props.MakeAppointmentMeHeySequence?.AppointmentLoading,
  };

  const isSnapshotCurrent = (snapshot: ReviewSnapshot) => {
    const live = liveRef.current;
    return mountedRef.current && !!live?.selectedDsb && !live.scheduleLoading &&
      !!cardAppointmentPackageRef.current?.isReady &&
      String(live.patient?.id) === String(live.formData.patientID) &&
      isAppointmentTimeAvailable(live.selectedDsb, live.formData.appointmentTime) &&
      signature(live.formData, live.selectedDsb, live.patient, cardAppointmentPackageRef.current, live.packageData) === snapshot.signature;
  };

  const handleSubmit = () => {
    const currentSelection = cardAppointmentPackageRef.current;
    if (!canSave || !currentSelection?.isReady) return;
    const checkedProductIds = [...(currentSelection.checkedProductIds || [])];
    const currentItems = getValidPackageSelection(currentSelection.items, checkedProductIds);
    if (!currentItems.length || currentItems.length !== new Set(checkedProductIds).size) {
      setErrors((current) => ({ ...current, packageItems: true }));
      return;
    }
    const items = (currentSelection.items || []).map((item) => ({
      ...item, original: item.original ? { ...item.original } : item.original,
    }));
    setSaveResult(null);
    reviewTriggerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setConfirmation({
      form: { ...formData },
      dsb: { ...selectedDsb },
      packageData: { ...props.selectPackageBinder },
      items,
      checkedProductIds,
      signature: signature(formData, selectedDsb, props.selectedPatient, currentSelection, props.selectPackageBinder),
    });
  };

  const handleConfirm = () => {
    const snapshot = confirmation;
    if (!snapshot || submittingRef.current || saveLocked) return;
    if (!isSnapshotCurrent(snapshot)) {
      setConfirmation(null);
      setSaveResult({ status: "unavailable" });
      return;
    }
    submittingRef.current = true;
    setIsLoading(true);
    let completed = false;
    const onResult = (result: PackageAppointmentResult) => {
      if (completed) return;
      completed = true;
      submittingRef.current = false;
      if (!mountedRef.current) return;
      setIsLoading(false);
      setConfirmation(null);
      setSaveResult(result.status === "success" ? null : result);
      if (result.status === "unavailable") {
        setFormData((current) => ({ ...current, appointmentTime: "" }));
        const currentDate = props.MakeAppointmentMeHeySequence?.currentDate;
        if (currentDate) props.runSequence({ sequence: "MakeAppointmentMeHey", action: "BATCH_DL", currentDate });
      }
    };
    try {
      const pending = props.runSequence({
        sequence: "MakeAppointmentMeHey",
        action: "PACKAGE_APPOINTMENT",
        appointmentData: snapshot.form,
        dsb: snapshot.dsb,
        items: snapshot.items,
        checkedProductIds: snapshot.checkedProductIds,
        package: snapshot.packageData,
        isCurrent: () => isSnapshotCurrent(snapshot),
        onResult,
        onSuccess: props.onAppointmentSuccess,
      });
      if (pending && typeof pending.catch === "function") {
        pending.catch(() => onResult({ status: "uncertain" }));
      }
    } catch {
      onResult({ status: "uncertain" });
    }
  };

  const mapOptions = useCallback(
    (items: any) =>
      items.map((item: any) => ({
        key: item.id,
        value: item.id,
        text: [item.pre_name_th, item.first_name_th, item.last_name_th].filter(Boolean).join(" "),
      })),
    []
  );

  return (
    <div className="package-right-panel-me-hey" style={{ padding: "2rem 1rem" }}>
      {/* Header Bar */}
      <div>
        <Label size="large">{headerText || "-"}</Label>
      </div>

      {/* Error Modal */}
      {props.MakeAppointmentMeHeySequence?.showError && (
        <ModalMakeAppointmentError
          open={props.MakeAppointmentMeHeySequence.showError}
          title="ไม่สามารถเพิ่มรายการนัดหมายใหม่ได้"
          subtitle={
            props.MakeAppointmentMeHeySequence.errorMessage || "กรุณาลองใหม่อีกครั้งในภายหลัง"
          }
          onClose={() => {
            props.runSequence({
              sequence: "MakeAppointmentMeHey",
              action: "CLOSE_ERROR_MODAL",
            });
          }}
        />
      )}

      {/* Form Content */}
      <div className="ui form" style={{ marginTop: "2rem" }}>
        <Form>
          {/* Appointment Time */}
          <div style={{ display: "flex", alignItems: "center" }}>
            <Form.Field required error={errors.appointmentTime}>
              <label>เวลานัดหมาย</label>
              {formData.manualTime ? (
                <Input
                  fluid
                  type="time"
                  value={formData.appointmentTime || ""}
                  disabled={!validSchedule || isDisabledStatus || isLoading}
                  style={{ width: "130px" }}
                  onChange={(e, { value }) => {
                    if (!isDisabledStatus) handleChange("appointmentTime", value);
                  }}
                  placeholder="HH:mm"
                />
              ) : (
                <Dropdown
                  fluid
                  selection
                  options={appointmentTimeSlots}
                  value={formData.appointmentTime || ""}
                  disabled={!validSchedule || isDisabledStatus || isLoading}
                  aria-label={intl.formatMessage({ id: "เวลานัดหมาย" })}
                  placeholder={intl.formatMessage({ id: "กรุณาเลือกเวลานัดหมาย" })}
                  onChange={(e, { value }) => handleChange("appointmentTime", value)}
                  {...(isDisabledStatus && { open: false })}
                />
              )}
            </Form.Field>
            <Checkbox
              label="กำหนดเวลาแบบ manual"
              aria-label="กำหนดเวลาแบบ manual"
              checked={formData.manualTime}
              disabled={!validSchedule || isDisabledStatus || isLoading}
              style={{ marginLeft: "1rem" }}
              onChange={(e, { checked }) => {
                if (!isDisabledStatus) {
                  setFormData((current) => ({ ...current, manualTime: checked, appointmentTime: "" }));
                }
              }}
            />
          </div>

          {/* Patient Name */}
          <Form.Field required error={errors.patientName}>
            <label>ชื่อผู้รับบริการ</label>
            {props.editingAppointment ? (
              <Input value={patientFullName || "-"} readOnly />
            ) : (
              <SearchBoxDropdown
                onEvent={props.onEvent}
                type="OfficialAccountPatientList"
                id={AMH}
                style={{ width: "100%" }}
                fluid={true}
                useWithKey={true}
                inline={true}
                icon="search"
                placeholder="ค้นหาชื่อ - นามสกุล"
                limit={20}
                mapOptions={mapOptions}
                searchedItemListWithKey={props.searchedItemListWithKey}
                selectedItem={formData.patientID || null}
                defaultText={formData.patientName}
                setSelectedItem={handleSelectedItem()}
                onAdditionalUrlParams={() => ({ active: true })}
                disabled={true}
              />
            )}
          </Form.Field>

          {/* Phone Number */}
          <Form.Field>
            <label>เบอร์โทรติดต่อ</label>
            <Input value={phoneNumber} disabled={true} readOnly />
          </Form.Field>
        </Form>

        {/* --- */}
      </div>

      <CardAppointmentPackage
        apiToken={props.apiToken}
        division={props.division}
        ref={cardAppointmentPackageRef}
        setProp={props.setProp}
        // data
        data={props.selectPackageBinder?.items || EMPTY_PACKAGE_ITEMS}
        buttonLoadCheck={props.buttonLoadCheck}
        onSelectionStateChange={handlePackageSelectionChange}
        // config
        selectable={true}
        isEdit={true}
        hideHistory={true}
        showQty={true}
        isPackageBinder={true}
        selectPromptText={intl.formatMessage({ id: "กรุณาเลือกรายการที่ต้องการนัดหมาย" })}
        languageUX={props.languageUX}
      />

      <Modal
        open={!!confirmation}
        role="dialog"
        aria-modal="true"
        aria-labelledby="package-appointment-review-title"
        size="tiny"
        onMount={() => document.getElementById("package-appointment-review-back")?.focus()}
        onUnmount={() => reviewTriggerRef.current?.focus()}
        onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
          if (event.key !== "Tab") return;
          const controls = Array.from(event.currentTarget.querySelectorAll<HTMLButtonElement>("button:not([disabled])"));
          if (!controls.length) { event.preventDefault(); return; }
          const first = controls[0];
          const last = controls[controls.length - 1];
          if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
          else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
        }}
        closeOnDimmerClick={!isLoading}
        closeOnEscape={!isLoading}
        onClose={() => { if (!isLoading) setConfirmation(null); }}
      >
        <Modal.Header id="package-appointment-review-title">{intl.formatMessage({ id: "ตรวจสอบข้อมูลนัดหมาย" })}</Modal.Header>
        <Modal.Content>
          <p><strong>{intl.formatMessage({ id: "ชื่อผู้รับบริการ" })}:</strong> {confirmation?.form.patientName}</p>
          <p><strong>{intl.formatMessage({ id: "ผู้ให้บริการ" })}:</strong> {confirmation?.dsb.doctor_provider_name}</p>
          <p><strong>{intl.formatMessage({ id: "วันที่นัดหมาย" })}:</strong> {confirmation?.form.date}</p>
          <p><strong>{intl.formatMessage({ id: "เวลานัดหมาย" })}:</strong> {confirmation?.form.appointmentTime}</p>
        </Modal.Content>
        <Modal.Actions>
          <Button id="package-appointment-review-back" disabled={isLoading} onClick={() => setConfirmation(null)}>
            {intl.formatMessage({ id: "กลับไปแก้ไข" })}
          </Button>
          <Button primary loading={isLoading} disabled={isLoading} onClick={handleConfirm}>
            {intl.formatMessage({ id: "ยืนยันบันทึกนัดหมาย" })}
          </Button>
        </Modal.Actions>
      </Modal>

      {saveResult && (
        <div role="alert" style={{ color: "#b03060" }}>
          {intl.formatMessage({
            id: saveResult.status === "partial"
              ? "สร้างนัดแล้ว แต่ข้อมูลประกอบยังอัปเดตไม่ครบ กรุณาตรวจสอบก่อนทำรายการอีกครั้ง"
              : saveResult.status === "uncertain"
                ? "ไม่ทราบผลการสร้างนัด กรุณาตรวจสอบรายการนัดก่อนทำรายการอีกครั้ง"
                : saveResult.status === "unavailable"
                  ? "ไม่สามารถยืนยันช่วงเวลานี้ได้ กรุณาเลือกตารางและเวลานัดใหม่"
                  : "ไม่สามารถบันทึกนัดหมายได้ กรุณาตรวจสอบข้อมูล",
          })}
          {saveResult.appointmentId ? ` (#${saveResult.appointmentId})` : ""}
        </div>
      )}
      {!validPatient && (
        <div style={{ color: "#767676" }}>
          {intl.formatMessage({ id: "ไม่พบข้อมูลผู้รับบริการ กรุณาตรวจสอบ" })}
        </div>
      )}
      {!validSchedule && (
        <div style={{ color: "#767676" }}>
          {intl.formatMessage({ id: "กรุณาเลือกตารางและเวลานัดหมาย" })}
        </div>
      )}
      {validSchedule && !validTime && (
        <div style={{ color: "#767676" }}>
          {intl.formatMessage({ id: "กรุณาเลือกเวลานัดหมายที่ว่าง" })}
        </div>
      )}

      {/* Footer Actions */}
      {(errors.packageItems || (packageSelection.isReady && packageSelection.selectedCount === 0)) && (
        <div role="alert" style={{ color: "#b03060" }}>
          {intl.formatMessage({ id: "กรุณาเลือกรายการนัดหมาย" })}
        </div>
      )}
      <div className="package-right-panel-me-hey__footer">
        <Button onClick={props.onClose} disabled={isLoading} onClose={props.onClose}>
          ปิดหน้าต่าง
        </Button>
        {!isDisabledStatus && (
          <Button
            onClick={handleSubmit}
            loading={isLoading}
            disabled={!canSave}
            style={{ backgroundColor: "var(--primary-theme-color)", color: "white" }}
          >
            {props.editingAppointment ? "แก้ไขนัดหมาย" : "บันทึกนัดหมาย"}
          </Button>
        )}
      </div>
    </div>
  );
};

export default PackageRightPanelMeHey;
